import type {
    FileExtractCategory,
} from '@/components/ai/ReferenceLayoutPdf/dto';
import { isUserLogin } from '@eureka/utils/account';
import {
    PsHttpService,
    type PatSnapCommonResponseData,
    type CoreDataError,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { buildSarDebugRecordsRequestData } from './sar-debug-api-payload';

/**
 * SAR API接口定义
 */
export interface SarPatentSuggestItem {
    patent_id: string
    patent_no: string
    title?: string
    current_assignee?: string[]
    structure_count?: number
    country?: string
    publication_date?: string
}

export interface SarPatentDetailItem {
    patent_id: string;
    patent_no: string;
    original_assignee: string[];
    country: string;
    publication_date: string;
    family_id: string;
    title: string;
    structure_count: number;
}

export interface SarApi {
    /**
     * 获取SAR文件列表
     */
    getSarFileList(agentJobId: string): Promise<SarFileListResponse>;

    /**
     * 重新提取SAR文件
     */
    retryExtractFile(agentJobId: string, fileIds: string[]): Promise<boolean>;

    /**
     * 获取SAR文件布局
     */
    getSarFileLayout(
        agentJobId: string,
    ): Promise<SarFileLayoutResponse>;

    /**
     * 获取SAR文件详情
     */
    getSarFileDetail(
        taskId: string,
        fileId: string,
    ): Promise<SarFileDetailResponse>;

    /**
     * 导出SAR数据
     */
    exportSarData(params: ExportSarDataParams): Promise<ExportSarDataResponse>;

    /**
     * 获取导出状态
     */
    getExportStatus(exportTaskId: string): Promise<GetExportStatusResponse>;

    /**
     * 获取精选案例
     */
    getFeaturedCases(key: string): Promise<SarFeaturedCaseItem[]>;

    /**
     * 搜索文件分组列表
     */
    getSarHistoryList(params: GroupSearchRequest): Promise<SarHistoryResponse>;

    /**
     * 编辑任务名称
     */
    updateGroupName(params: UpdateGroupNameRequest): Promise<void>;

    /**
     * 批量删除任务
     */
    deleteGroups(params: DeleteGroupsRequest): Promise<void>;

    /**
     * 修改 file 解析数据
     */
    putFileDetail(params: PutFileDetailParams): Promise<void>;
    /**
     * 专利号搜索建议
     */
    searchPatentSuggest(keyword: string): Promise<SarPatentSuggestItem[]>;

    /**
     * 获取专利详情
     */
    getPatentDetails(patentNos: string[]): Promise<SarPatentDetailItem[]>;

    /**
     * 获取SAR示例文件列表
     */
    getSarExamples(): Promise<SarExampleItem[]>;
    /**
     * 获取SAR分析列表
     */
    getSarAnalysisList(
        params: GetSarAnalysisListParams,
    ): Promise<GetSarAnalysisListResponse>;

    /**
     * 获取SAR比较数据
     */
    getSarComparison(
        params: GetSarComparisonParams,
    ): Promise<GetSarComparisonResponse>;

    /**
     * 获取SAR分享数据
     */
    getSarShareData(shareId: string): Promise<SarShareDataResponse>;

    /**
     * 复制文档分享接口
     */
    duplicateShare(params: DuplicateShareParams): Promise<DuplicateShareResponse | CoreDataError>;

    /**
     * 获取 SAR debug 中间记录
     */
    getSarDebugRecords(params: SarDebugRecordsRequest): Promise<SarDebugRecordsResponse>;
}

/**
 * SAR文件列表项
 */
export interface SarFileItem {
    file_size?: number;
    file_path?: string;
    file_group_id?: string;
    file_id: string;
    file_name: string;
    status?: 'PROCESSING' | 'SUCCESS' | 'FAILED';
    type?: 'PN' | 'FILE';
    input_value?: string
    layout?: {
        bbox: number[];
        page: number;
        type: string;
        category: FileExtractCategory;
        index: number;
    }[]
}

/**
 * 标准化靶点
 */
export interface SarStandardizedTarget {
    target_id: string;
    display_name_cn: string;
    display_name_en: string;
}

/**
 * 给药方案
 */
export interface SarDosingRegimen {
    route: string;
    dosage: string;
    frequency: string;
    duration: string;
}

/**
 * 结构式
 */
export interface SarStructure {
    smiles?: string;
    mol?: string;
}

/**
 * 结构来源
 */
export interface SarStructureSource {
    page: number | null;
    bbox: number[] | null;
    category: string | null;
    index: number | null;
}

/**
 * SAR v2 结果中的位置字段。后端历史字段可能同时存在 snake_case 和 camelCase。
 */
export interface SarResultLocation {
    label?: string;
    value_source?: string;
    valueSource?: string;
    dc_value_source?: string;
    dcValueSource?: string;
    type?: string;
    page?: number | string | null;
    page_number?: number | string | null;
    pageNumber?: number | string | null;
    index?: number | string | null;
    bbox?: number[] | string | null;
    url?: string;
    source?: string;
    quality?: string;
    substance_image?: string;
    substanceImage?: string;
    patent_image?: string;
    patentImage?: string;
    iupac_name?: string;
    iupacName?: string;
}

/**
 * 化合物编号
 */
export interface SarSubstanceCode {
    code: string;
    image?: string;
}

/**
 * SAR数据项DTO (API snake_case格式)
 */
export interface SarItemDto {
    result_id: string;
    substance_code: SarSubstanceCode;
    indicator: string;
    value: string;
    unit: string;
    experimental_subject: string;
    experimental_method: string;
    experimental_purpose: string;
    dosing_regimen: SarDosingRegimen | null;
    experimental_target: string;
    target?: string[];
    standardized_target: SarStandardizedTarget[] | null;
    other_name: string;
    structure: SarStructure | null;
    match: boolean;
    source: SarStructureSource | null;
    value_source: string;
    protein_subtype: string;
    substance_code_raw?: string;
    substanceCodeRaw?: string;
    activity_location?: SarResultLocation | null;
    activityLocation?: SarResultLocation | null;
    record?: string;
    activity_quality?: string;
    activityQuality?: string;
    activity_confidence?: string;
    activityConfidence?: string;
    activity_review_status?: string;
    activityReviewStatus?: string;
    activity_confidence_reason?: string;
    activityConfidenceReason?: string;
    structure_quality?: string;
    structureQuality?: string;
    iupac_name?: string;
    iupacName?: string;
    structure_source?: string;
    structureSource?: string;
    structure_location?: SarResultLocation | null;
    structureLocation?: SarResultLocation | null;
    iupac_location?: SarResultLocation | null;
    iupacLocation?: SarResultLocation | null;
    sar_extraction_mode?: string;
    sarExtractionMode?: string;
}

/**
 * SAR文件列表响应
 */
export interface SarFileListResponse {
    item_status: SarFileItem[];
    owner_id: string;
}

export interface SarFileLayoutResponse {
    file_group_id: string;
    items: {
        file_id: string;
        layout: {
            bbox: number[];
            page: number;
            type: string;
            category: FileExtractCategory;
            index: number;
        }[];
    }[];
}

/**
 * SAR文件详情响应
 */
export interface SarFileDetailResponse {
    items: SarItemDto[];
    total: number;
}

export interface ExportSarDataParams {
    task_id: string;
    export_type: 'csv' | 'excel' | 'excel_merge' | 'sdf';
    files: { business_id: string; result_ids: string[] }[];
    sorts: { field: 'source.page' | 'value' | 'substance_code.code'; order: 'asc' | 'desc' }[];
}

export interface ExportSarDataResponse {
    export_task_id: string;
}

export interface GetExportStatusResponse {
    status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
    export_file_path: string | null;
}

type ILocale = 'cn' | 'en' | 'jp' | 'tw'
export interface SarFeaturedCaseTag {
    label: Record<ILocale, string>;
    type: 'target' | 'indication' | 'company' | 'drug';
}

export interface SarFeaturedCaseItem {
    coverVisual: Record<ILocale, string>;
    tags: SarFeaturedCaseTag[];
    title: Record<ILocale, string>;
    subTitle: Record<ILocale, string>;
    statCount?: Record<ILocale, string>;
    companies?: Record<ILocale, string>;
    agentName: Record<ILocale, string>;
    skillName?: Record<ILocale, string>;
    detailUrl: Record<ILocale, string>;
}

// SAR Analysis types
export interface GetSarAnalysisListParams {
    input_data: {
        compound_code: string;
        structure: string;
        value: string;
    }[];

    type: string;
}

export interface GetSarComparisonParams extends GetSarAnalysisListParams {
    /**
     * 相似度阈值（0.0-1.0），如果为 null 则自动计算
     */
    similarity_limit?: number;
}

export interface SarAnalysisRowResult {
    compound_code: string;
    structure: string;
    value: string;
    properties: {
        properties?: {
            cLogP?: number;
            'Polar Surface Area'?: number;
            Druglikeness?: number;
            LE?: number;
            LLE?: number;
        };
        mutagenic_text?: string;
    };
    rgroup?: {
        scaffold?: string;
        substituents?: Record<string, string>;
    };
    ring_systems?: string;
    row_index: number;
}

export interface ScaffoldFrequency {
    smiles: string;
    frequency: number;
}

export interface GetSarAnalysisListResponse {
    row_results: SarAnalysisRowResult[];
    scaffold_frequency: ScaffoldFrequency[];
}

export interface SarComparisonItem {
    smiles1: string;
    smiles2: string;
    similarity: number;
    activity_dif?: number;
    compound_code1: string;
    compound_code2: string;
}

export interface GetSarComparisonResponse {
    similar_pairs: SarComparisonItem[];
}

interface SarFileExampleItem {
    type: 'FILE' | 'TEXT'
    id: string
    name: string
    size: number
    file_type: string
    url: string
}

export interface SarShareDataResponse {
    share_user_id: string;
    share_time: number;
    share_user_name: string;
    file_detail: {
        file_group_id: string
        items: SarFileItem[]
    }
    sar_data: (SarFileDetailResponse & {
        file_id: string
        business_id: string
    })[]
}

export interface SarDebugRecordsRequest {
    file_id?: string;
    file_group_id?: string;
    workflow_id?: string;
    step_name?: string;
    record_type?: string;
    page?: number;
    page_size?: number;
}

export interface SarDebugRecordResponse {
    id?: number | string;
    fileId?: string;
    file_id?: string;
    workflowId?: string;
    workflow_id?: string;
    stepName?: string;
    step_name?: string;
    recordType?: string;
    record_type?: string;
    data?: unknown;
    createdAt?: string;
    created_at?: string;
}

export interface SarDebugRecordsResponse {
    records: SarDebugRecordResponse[];
    total: number;
    page?: number;
    pageSize?: number;
    page_size?: number;
}

interface DuplicateShareParams {
    /**
     * share 分享, file_group 文件组
     */
    source_type: 'share' | 'file_group'
    /**
     * 分享ID 或 文件组ID
     */
    source_id: string
    /**
     * 'LS', 'IP, 'RD', 'MSE'
     */
    business_type: string
}

/**
 * 复制文档分享接口返回
 */
export type DuplicateShareResponse = {
    file_group_id: string
  }

export type SarExampleItem = SarFileExampleItem

export const SarApi = Symbol('SarApi');

/**
 * SAR API实现
 */
@injectable()
export class SarApiImpl extends PsHttpService implements SarApi {
    get baseUrl() {
        return `${import.meta.env.VITE_EUREKA_SERVICE_URI}/core-eurekals-api/eurekals_agent/1.0`;
    }

    get options() {
        return {
            baseURL: this.baseUrl,
        };
    }

    private async _getBaseUrl(path: string) {
        const isLogin = await isUserLogin();
        const base = isLogin ? '' : '/guest';
        return `${base}${path}`;
    }

    /**
     * 获取SAR文件列表
     */
    async getSarFileList(agentJobId: string): Promise<SarFileListResponse> {
        return this.post<PatSnapCommonResponseData<SarFileListResponse>>({
            url: await this._getBaseUrl('/file/group/status'),
            data: {
                file_group_id: agentJobId,
            },
        }).then((res) => {
            return res;
        }) as Promise<SarFileListResponse>;
    }

    /**
     * 重新提取SAR文件
     */
    async retryExtractFile(agentJobId: string, fileIds: string[]): Promise<boolean> {
        return this.post<PatSnapCommonResponseData<boolean>>({
            url: await this._getBaseUrl('/file/extract/retry'),
            data: {
                file_group_id: agentJobId,
                file_ids: fileIds,
            },
        }).then((res) => {
            return res;
        }) as Promise<boolean>;
    }

    /**
     *
     * @param agentJobId
     * @returns
     */
    async getSarFileLayout(
        agentJobId: string
    ): Promise<SarFileLayoutResponse> {
        return this.get<PatSnapCommonResponseData<SarFileLayoutResponse>>({
            url: await this._getBaseUrl(`/file/group/${agentJobId}`),
        }).then((res) => {
            return res;
        }) as Promise<SarFileLayoutResponse>;
    }

    /**
     * 获取SAR文件详情
     */
    async getSarFileDetail(
        taskId: string,
        fileId: string
    ): Promise<SarFileDetailResponse> {
        return this.post<PatSnapCommonResponseData<SarFileDetailResponse>>({
            url: await this._getBaseUrl('/dg/file/detail'),
            data: {
                task_id: taskId,
                business_id: [fileId],
            },
        }).then((res) => {
            return res;
        }) as Promise<SarFileDetailResponse>;
    }

    async exportSarData(
        params: ExportSarDataParams
    ): Promise<ExportSarDataResponse> {
        return this.post<PatSnapCommonResponseData<ExportSarDataResponse>>({
            url: await this._getBaseUrl('/dg/export'),
            data: params,
        }).then((res) => res as unknown as ExportSarDataResponse);
    }

    async getExportStatus(
        exportTaskId: string
    ): Promise<GetExportStatusResponse> {
        return this.get<PatSnapCommonResponseData<GetExportStatusResponse>>({
            url: await this._getBaseUrl('/dg/export/status'),
            params: { export_task_id: exportTaskId },
        }).then((res) => res as unknown as GetExportStatusResponse);
    }

    async getFeaturedCases(key: string): Promise<SarFeaturedCaseItem[]> {
        return this.get<PatSnapCommonResponseData<SarFeaturedCaseItem[]>>({
            url: await this._getBaseUrl('/common/common-config'),
            params: { key },
        }).then((res) => (res as unknown as SarFeaturedCaseItem[]) ?? []);
    }

    async searchPatentSuggest(
        keyword: string,
        limit = 3
    ): Promise<SarPatentSuggestItem[]> {
        return this.get<PatSnapCommonResponseData<SarPatentSuggestItem[]>>({
            url: await this._getBaseUrl('/patent/pn/autocomplete'),
            params: { query: keyword, limit },
        }).then((res) => (res as unknown as SarPatentSuggestItem[]) ?? []);
    }

    async getPatentDetails(patentNos: string[]): Promise<SarPatentDetailItem[]> {
        return this.post<PatSnapCommonResponseData<SarPatentDetailItem[]>>({
            url: await this._getBaseUrl('/patent/pn/details'),
            data: { patent_nos: patentNos },
            cache: true,
        }).then((res) => (res as unknown as SarPatentDetailItem[]) ?? []);
    }

    async getSarAnalysisList(
        params: GetSarAnalysisListParams
    ): Promise<GetSarAnalysisListResponse> {
        return this.post<PatSnapCommonResponseData<GetSarAnalysisListResponse>>({
            url: await this._getBaseUrl('/sar_comprehensive/analyze'),
            data: params,
        }).then((res) => res as unknown as GetSarAnalysisListResponse);
    }

    async getSarComparison(
        params: GetSarAnalysisListParams
    ): Promise<GetSarComparisonResponse> {
        return this.post<PatSnapCommonResponseData<GetSarComparisonResponse>>({
            url: await this._getBaseUrl('/sar_activity_cliffs/analyze'),
            data: params,
        }).then((res) => res as unknown as GetSarComparisonResponse);
    }

    async getSarExamples(): Promise<SarExampleItem[]> {
        return this.get<PatSnapCommonResponseData<SarExampleItem[]>>({
            url: 'file/examples/sar',
        }).then(res => (res as unknown as SarExampleItem[]) ?? [])
    }

    async getSarHistoryList(params: GroupSearchRequest): Promise<SarHistoryResponse> {
        return this.post<PatSnapCommonResponseData<SarHistoryResponse>>({
            url: await this._getBaseUrl('/file/group/search'),
            data: params,
        }).then(res => res as unknown as SarHistoryResponse);
    }

    async updateGroupName(params: UpdateGroupNameRequest): Promise<void> {
        await this.put({
            url: await this._getBaseUrl('/file/group'),
            data: params,
        })
    }

    async deleteGroups(params: DeleteGroupsRequest): Promise<void> {
        await this.delete({
            url: await this._getBaseUrl('/file/group'),
            data: params,
        })
    }

    async putFileDetail(params: PutFileDetailParams): Promise<void> {
        await this.put({
            url: await this._getBaseUrl('/dg/file/detail'),
            data: params,
        })
    }

    async getSarShareData(shareId: string): Promise<SarShareDataResponse> {
        return this.get<PatSnapCommonResponseData<SarShareDataResponse>>({
            url: `public/file/share/${shareId}`,
        }).then((res) => res as unknown as SarShareDataResponse);
    }

    async duplicateShare(params: DuplicateShareParams): Promise<DuplicateShareResponse | CoreDataError> {
        return this.post<PatSnapCommonResponseData<DuplicateShareResponse | CoreDataError>>({
            url: await this._getBaseUrl('/file/share/save'),
            data: params,
        }).then((res) => res as unknown as DuplicateShareResponse | CoreDataError);
    }

    async getSarDebugRecords(
        params: SarDebugRecordsRequest
    ): Promise<SarDebugRecordsResponse> {
        return this.post<PatSnapCommonResponseData<SarDebugRecordsResponse>>({
            url: 'public/dg/debug/file/records',
            data: buildSarDebugRecordsRequestData(params),
        }).then((res) => res as unknown as SarDebugRecordsResponse);
    }
}

/**
 * sar提取历史表格
 */
export interface GroupSearchRequest {
    file_group_name?: string
    file_group_id?: string
    offset?: number
    limit?: number
}

export interface SarHistoryResponse {
    items: SarHistoryItem[]
    total: number
}
export interface SarHistoryItem {
    task_id: string
    task_name: string
    created_at: string
    files: FileItem[]
    status: 'PROCESSING' | 'SUCCESS' | 'FAILED'
}
export interface FileItem {
    file_name: string
    status: 'PROCESSING' | 'SUCCESS' | 'FAILED'
    file_id: string
}

export interface UpdateGroupNameRequest {
    file_group_id: string
    file_group_name: string
}

export interface DeleteGroupsRequest {
    file_group_ids: string[]
}

export interface PutFileDetailParams {
    items: SarItemDto[]
}
